<script lang="ts" setup>
import type { YMapFeature, LngLat } from '@yandex/ymaps3-types'
import type { FetchError } from 'ofetch'
import {
  YandexMap,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultMarker,
  YandexMapDefaultSchemeLayer,
  YandexMapFeature,
  type YandexMapDefaultMarkerSettings,
  type YandexMapSettings
} from 'vue-yandex-maps'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import BasicInput from '~/components/form-elements/BasicInput.vue'
import BasicTextarea from '~/components/form-elements/BasicTextarea.vue'
import TileMenu from '~/components/menus/TileMenu.vue'
import constants from '~/constants'
import menuItems from '~/constants/menu-items'
import Page from '~/enums/page'
import ApiErrorHandler from '~/helpers/api-error-handler'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'

const { $apiHelper, $toast } = useNuxtApp()

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]
const emptyForm = { name: null, email: null, phoneNumber: null, text: null }
const mapCoordinates = [37.514374, 55.610885] as LngLat
const mapSettings = {
  map: {
    location: { center: mapCoordinates, zoom: 16 }
  } as YandexMapSettings,
  mapDefaultMarker: {
    color: '#59ad3b', // main-green-color.
    coordinates: mapCoordinates,
    id: '1',
    subtitle: 'Голубинская',
    title: 'Склад iTAB',
  } as YandexMapDefaultMarkerSettings,
  mapFeature: {
    geometry: {
      coordinates: [
        [37.50839679353582, 55.61729169105121],
        [37.50845915489543, 55.617256778252646],
        [37.50849335306037, 55.617226039784605],
        [37.50852419846404, 55.61718505512286],
        [37.5095246624266, 55.616831750362884],
        [37.51023410671083, 55.616602916412084],
        [37.51057407670341, 55.616466678007626],
        [37.510763172438985, 55.616388122500744],
        [37.510993171862, 55.61628148410425],
        [37.51149943881348, 55.61601052256726],
        [37.51157923453167, 55.615964982621435],
        [37.512173290201545, 55.61557779191548],
        [37.51649164671524, 55.612354156595906],
        [37.516679401346366, 55.61130359019157],
        [37.514598007150795, 55.610432145202516],
        [37.51432173962226, 55.6105399382333],
        [37.51426138991935, 55.61058700268442],
        [37.514347220607874, 55.610624957872574],
        [37.51420372242558, 55.610867870125006]
      ] as LngLat[],
      type: 'LineString'
    },
    id: '2',
    style: {
      stroke: [{
        color: '#ff0048', // main-red-color.
        opacity: 0.75,
        width: 5
      }]
    }
  } as unknown as YMapFeature
}
const messages = {
  emailFieldCannotBeEmpty: 'Поле «Email» не может быть пустым.',
  failedToSendMessage: 'Не удалось отправить сообщение.',
  messageFieldCannotBeEmpty: 'Поле «Сообщение» не может быть пустым.',
  messageHasBeenSent: 'Сообщение отправлено.',
  nameFieldCannotBeEmpty: 'Поле «Имя» не может быть пустым.'
}
const title = 'Контакты'

const form = ref({ ...emptyForm })
const isLoading = ref(false)

useHead({
  title: `${title} — iTAB`,
  meta: [{
    name: 'description',
    content: 'Наши контакты. Мы будем рады вашему звонку.'
  }]
})

async function sendMessage () {
  const { name, email, phoneNumber, text } = form.value
  const {
    emailFieldCannotBeEmpty,
    messageFieldCannotBeEmpty,
    nameFieldCannotBeEmpty,

    failedToSendMessage,
    messageHasBeenSent
  } = messages

  if (!name) {
    $toast.error(nameFieldCannotBeEmpty)

    return
  }

  if (!email) {
    $toast.error(emailFieldCannotBeEmpty)

    return
  }

  if (!text) {
    $toast.error(messageFieldCannotBeEmpty)

    return
  }

  const data = { name, email, phoneNumber, text }

  isLoading.value = true

  try {
    await $apiHelper.messages.createMessage(data)

    form.value = { ...emptyForm }

    $toast.success(messageHasBeenSent)
  } catch (error) {
    ApiErrorHandler.handleFetchError(error as FetchError, failedToSendMessage)
  }

  isLoading.value = false
}
</script>

<template>
  <section>
    <div class="container">
      <Breadcrumbs :current="title" :items="breadcrumbsItems" />

      <div class="content-wrapper">
        <h1 class="mb-10">
          {{ title }}
        </h1>

        <div class="mb-10">
          <TileMenu :items="menuItems.contacts" />
        </div>

        <div class="h2">
          Самовывоз
        </div>

        <ul class="dashed mb-6">
          <li>Адрес: г. Москва, ул. Голубинская, д.&nbsp;4А, стр.&nbsp;2, эт.&nbsp;2, офис 202&nbsp;— ст.&nbsp;м. «Тёплый стан».</li>
          <li>Режим работы: ежедневно, 10:00&nbsp;— 18:00.</li>
          <li>Тел.: <a class="whitespace-nowrap" :href="constants.phoneNumbers.mainWarehouse.href">{{ constants.phoneNumbers.mainWarehouse.formattedText }}</a>.</li>
        </ul>

        <div class="mb-10 h-[400px] overflow-hidden rounded-xl border border-solid border-main-gray">
          <YandexMap class="size-full" :settings="mapSettings.map">
            <YandexMapDefaultFeaturesLayer />
            <YandexMapDefaultMarker :settings="mapSettings.mapDefaultMarker" />
            <YandexMapDefaultSchemeLayer />
            <YandexMapFeature :settings="mapSettings.mapFeature" />
          </YandexMap>
        </div>

        <div class="h2">
          Обратная связь
        </div>

        <div class="row mb-6">
          <div class="column-4 tb12 with-margin-bottom-24">
            <BasicInput v-model="form.name" label-text="Имя" />
          </div>
          <div class="column-4 tb12 with-margin-bottom-24">
            <BasicInput v-model="form.email" label-text="Email" />
          </div>
          <div class="column-4 tb12">
            <BasicInput v-model="form.phoneNumber" label-text="Номер телефона" />
          </div>
        </div>

        <BasicTextarea v-model="form.text" class="mb-6" label-text="Сообщение" />

        <BasicButton color="green" :is-loading="isLoading" title="Отправить" @click="sendMessage" />
      </div>
    </div>
  </section>
</template>
